import useGetApi from 'src/api/useGetApi';
import { SalesOrderSource } from '@erp/shared-types';

export interface Notes {
  text: string;
  createdAt: Date;
  userId: string;
}

export enum SalesOrderStatus {
  pending = 'pending',
  in_progress = 'in_progress',
  fulfilled = 'fulfilled',
  closed = 'closed',
}

export enum SalesOrderSyncStatus {
  not_started = 'not_started', // sales order created, but not queued
  pending = 'pending', // queued for sync
  in_progress = 'in_progress', // sync in progress
  success = 'success', // sync succeeded
  failed = 'failed', // sync failed
}

// SO Line Item
export enum SalesOrderItemType {
  customer_purchase_order_line_item = 'customer_purchase_order_line_item',
  direct_entry = 'direct_entry',
}

// New interface for EDI acknowledgment history entries
export interface EdiAcknowledgmentEntry {
  timestamp: string; // ISO date string
  statusCode: string; // LINE_ITEM_STATUS_CODE value
  acknowledgedValues: {
    quantity?: number | null;
    unitPrice?: number | null;
    deliveryDate?: string | null;
    lineTotal?: number | null;
  };
  orderfulMessageId?: string | null; // Reference to the outbound EDI message
  notes?: string | null;
}

export interface SalesOrderLineItemAttributes {
  id: string;
  organizationId: string;
  salesOrderId: string;
  salesOrderItemType: SalesOrderItemType;
  customerPurchaseOrderLineItemId?: string | null;
  customerProductId?: string | null;
  productId?: string | null;
  productVariantId?: string | null;
  quantity?: number | null;
  unitPrice?: number | null;
  lineTotal: number;
  rescheduledDate?: string | null;
  ediRejected?: boolean | null;
  originalPo1Loop?: Record<string, unknown> | null;
  ediAcknowledgmentHistory?: EdiAcknowledgmentEntry[] | null;
  createdAt: Date;
  updatedAt: Date;
}

export interface SalesOrderDTO {
  id: string;
  internalId: number;
  organizationId: string;
  customerId: string;
  customerPurchaseOrderId?: string | null;
  customerPurchaseOrderNumber?: string | null;
  stagedCustomerPurchaseOrderId?: string | null;
  source: SalesOrderSource;
  inboundEmailId?: string | null;
  creatorId?: string | null;
  orderDate: Date;
  deliveryDate?: Date | null;
  shipDate?: Date | null;
  status: SalesOrderStatus;
  totalAmount?: number | null;
  currencyCode?: string | null;
  notes?: Notes[] | null;
  originId?: string | null;
  originSystem?: string | null;
  originEntity?: string | null;
  originSyncStatus?: SalesOrderSyncStatus | null;
  originSyncErrorMessage?: string | null;
  originMetadata?: Record<string, unknown> | null;
  shipToLocation: {
    DUNS?: string;
    locationName?: string;
    address?: string;
  } | null;
  createdAt: Date;
  updatedAt: Date;

  salesOrderLineItems: SalesOrderLineItemAttributes[];
  customer?: {
    name: string;
    address?: string | null;
    phoneNumber?: string | null;
    emailDomain?: string | null;
    contactPerson?: string | null;
    paymentTerms?: string | null;
    creditLimit?: number | null;
  };
}

type ApiResponse = {
  salesOrder: SalesOrderDTO;
};

const useSalesOrder = (salesOrderId?: string) => {
  return useGetApi<ApiResponse>({
    endpoint: `accounts-receivable/sales-orders/${salesOrderId}`,
    options: {
      enabled: !!salesOrderId,
    },
  });
};

export default useSalesOrder;
